import { useCallback, useContext, useRef } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import AnalyticsReactContext from '@atlaskit/analytics-next/AnalyticsReactContext';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ANALYTICS_CHANNEL } from '../common/ANALYTICS_CHANNEL';
import { LIBRARY_ATTRIBUTE, type EventPayload } from '../common/types';
import { getAttributesFromContexts } from '../common/utils/getAttributesFromContexts';
import { getDefaultTrackEventConfig } from '../common/utils/getDefaultTrackEventConfig';

const globalEventConfig = getDefaultTrackEventConfig();

export const useRovoAgentActionAnalytics = <T extends {}>(
	commonAttributes: T,
): {
	trackAgentEvent: (payload: EventPayload) => void;
} => {
	const analyticsContext = useContext(AnalyticsReactContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const commonAttributesRef = useRef(commonAttributes);

	const fireAnalyticsEvent = useCallback(
		(event: AnalyticsEventPayload) => {
			const attributes = {
				...getAttributesFromContexts(analyticsContext.getAtlaskitAnalyticsContext()),
				...commonAttributesRef.current,
				library: LIBRARY_ATTRIBUTE,
				...event.attributes,
			};

			createAnalyticsEvent({
				...globalEventConfig,
				...event,
				attributes,
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, analyticsContext], // keep number of dependencies minimal to prevent re-rendering
	);

	/**
	 * Fully-typed event tracking using discriminated union payload types.
	 * The payload type enforces correct action, actionSubject, and attributes.
	 */
	const trackAgentEvent = useCallback(
		(payload: EventPayload): void => {
			const { action, actionSubject, attributes, ...eventProps } = payload;

			fireAnalyticsEvent({
				actionSubject,
				action,
				...eventProps,
				attributes,
			});
		},
		[fireAnalyticsEvent],
	);

	return {
		trackAgentEvent,
	};
};
